import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// One effective configuration key/value as returned by GET /api/admin/configuration.
// Secret-like values are already redacted server-side ("********") and flagged with isSecret so the
// UI can offer an explicit "show" action that fetches the real value on demand.
export interface ConfigurationEntry {
  key: string;
  value: string;
  isSecret: boolean;
  // Client-only reveal state: the fetched real value and an in-flight guard for the show action.
  revealedValue?: string;
  revealing?: boolean;
}

// Owns the admin Configuration panel: lists every effective configuration parameter the app has
// loaded (appsettings, environment variables, user-secrets, etc.), with secrets redacted. Mirrors
// the other singleton admin services — shared state lives here and the panel binds to it directly.
// Read-only: an audit view, not an editor.
@singleton()
export class ConfigAdminService {
  private readonly http: HttpClient = resolve(HttpClient);

  panelOpen: boolean = false;
  loading: boolean = false;
  error: string | null = null;
  entries: ConfigurationEntry[] = [];
  search: string = '';

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const response = await this.http.get('/api/admin/configuration');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.entries = await response.json();
    } catch (e: any) {
      this.error = `Could not load configuration: ${e.message}`;
      this.entries = [];
    } finally {
      this.loading = false;
    }
  }

  // The entries matching the current search filter (key or value substring, case-insensitive).
  get filteredEntries(): ConfigurationEntry[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.entries;
    return this.entries.filter(
      (e) => e.key.toLowerCase().includes(term) || e.value.toLowerCase().includes(term)
    );
  }

  // Fetches and reveals the real value for a masked secret entry, on demand.
  async reveal(entry: ConfigurationEntry): Promise<void> {
    if (entry.revealing || entry.revealedValue !== undefined) return;
    entry.revealing = true;
    this.error = null;
    try {
      const response = await this.http.get(
        `/api/admin/configuration/value?key=${encodeURIComponent(entry.key)}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: { key: string; value: string } = await response.json();
      entry.revealedValue = data.value;
    } catch (e: any) {
      this.error = `Could not reveal value: ${e.message}`;
    } finally {
      entry.revealing = false;
    }
  }

  // Re-masks a previously revealed secret entry.
  hide(entry: ConfigurationEntry): void {
    entry.revealedValue = undefined;
  }

  async openPanel(): Promise<void> {
    this.panelOpen = true;
    this.search = '';
    await this.load();
  }

  closePanel(): void {
    this.panelOpen = false;
    this.error = null;
  }
}
